// Regular version of PartyDisplayPage
// All phone mic code, UI, and references are removed in this version.
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { updateDoc, getDoc, doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useParty } from '../contexts/PartyContext';
import { useAuth } from '../contexts/AuthContext';
import YouTubePlayer from '../components/karaoke/YouTubePlayer';
import CdgPlayer from '../components/karaoke/CdgPlayer';
import SplashScreen from '../components/karaoke/SplashScreen';
import QueueDisplay from '../components/karaoke/QueueDisplay';
import QRCodeDisplay from '../components/karaoke/QRCodeDisplay';
import QrOkeAd from '../components/QrOkeAd';
import CastOptionsModal from '../components/CastOptionsModal';
import VenueAdOverlay from '../components/VenueAdOverlay';
import HostControlsModal from '../components/karaoke/HostControlsModalRegular';
import UserSongManager from '../components/UserSongManager';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import { useHostBranding } from '../hooks/useHostBranding';
import { usePartyAdsAllowed } from '../hooks/usePartyAdsAllowed';
import { useVenueAdsConfig } from '../hooks/useVenueAdsConfig';
import { advanceReducedPartySong, joinReducedParty, mapPartySnapshot, startReducedPartySong } from '../services/reducedPartyActions';
import { reducedModeEnabled } from '../utils/reducedMode';
import { AdTypeKey, adTypeKeys } from '../utils/adSystem';

const PartyDisplayPageRegular: React.FC = () => {
	const { partyId } = useParams<{ partyId: string }>();
	const [searchParams] = useSearchParams();
	const { currentParty, songQueue, currentSong, isPartyOwner, setCurrentParty } = useParty();
	const [splashPaused, setSplashPaused] = useState(false);
	const navigate = useNavigate();
	const { user } = useAuth();
	const [showTitleScreen, setShowTitleScreen] = useState(true);
	const [showSplash, setShowSplash] = useState(false);
	const [splashCountdown, setSplashCountdown] = useState(0);
	const [videoDuration, setVideoDuration] = useState<number | null>(null);
	const [playbackStarted, setPlaybackStarted] = useState(false);
	const [showQrBox, setShowQrBox] = useState(true);
	const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
	const [showAd, setShowAd] = useState(false);
	const [adType, setAdType] = useState<AdTypeKey>('house');
	const [adDurationSeconds, setAdDurationSeconds] = useState<number | undefined>(undefined);
	// Local track (host-only playback, supports MP4 and CD+G)
	const [localTrack, setLocalTrack] = useState<{ kind?: 'cdg'|'file'; url?: string; name?: string; mp3Url?: string; cdgBuffer?: ArrayBuffer } | null>(null);
	const localVideoRef = useRef<HTMLVideoElement | null>(null);
	const [needsPlaybackUnlock, setNeedsPlaybackUnlock] = useState(false);
	// Venue Ad state listener
	const [scheduledAd, setScheduledAd] = useState<{ type?: 'venue' | 'house'; status: 'scheduled' | 'playing' | 'played' | 'cancelled'; durationSeconds?: number } | null>(null);
	const venueAdActiveRef = useRef(false);
	const [showHostControls, setShowHostControls] = useState(false);
	const [showMicCoach, setShowMicCoach] = useState(false);
	const [toasts, setToasts] = useState<Array<{ id: number; text: string; type?: 'info' | 'success' | 'error' }>>([]);
	const nextToastId = useRef<number>(1);
	const pushToast = useCallback((text: string, type: 'info' | 'success' | 'error' = 'info', ttl = 4000) => {
		const id = nextToastId.current++;
		setToasts((prev) => [...prev, { id, text, type }]);
		window.setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), ttl);
	}, []);
	const [showUserSongManager, setShowUserSongManager] = useState(false);
    const [showCastOptions, setShowCastOptions] = useState(false);
	const prevSongId = useRef<string | null>(null);
	const heartbeatTimer = useRef<number | null>(null);
	const countdownTimer = useRef<NodeJS.Timeout | null>(null);
	const adTimer = useRef<NodeJS.Timeout | null>(null);

	// Host branding and premium status
	const hostUserId = currentParty?.createdBy;
	const { branding: hostBranding, isPremium: isPremiumHost } = useHostBranding(hostUserId);
	const { config: venueConfig } = useVenueAdsConfig(hostUserId, partyId || null);
	const [showVenueAd, setShowVenueAd] = useState(false);
	const lastVenueAdAtRef = useRef<number>(0);
	const songsSinceVenueAdRef = useRef<number>(0);
	const schedulingRef = useRef<boolean>(false);
    const splashPendingRef = useRef<boolean>(false);

	// Whether this window is an external display (view-only)
	const forceViewerDisplay = useMemo(() => {
        const v = (searchParams.get('display') || '').toLowerCase();
        return v === '1' || v === 'true' || v === 'yes';
    }, [searchParams]);

	// External display presence and explicit host request
	const isExternalDisplay = forceViewerDisplay;
	const [externalActive, setExternalActive] = useState<boolean>(false);
	const [externalRequested, setExternalRequested] = useState<boolean>(false);
	const isMobileHost = React.useMemo(() => {
		if (!isPartyOwner) return false;
		if (typeof navigator === 'undefined') return false;
		const ua = navigator.userAgent || '';
		return /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
	}, [isPartyOwner]);

	// Watch external display presence (heartbeat)
	useEffect(() => {
		if (!partyId) return;
		if (isExternalDisplay) return; // host window watches presence
		const ref = doc(db, 'parties', partyId, 'control', 'displayPresence');
		const unsub = onSnapshot(ref, (snap) => {
			if (!snap.exists()) { setExternalActive(false); return; }
			const d = snap.data() as any;
			const ts = d?.lastSeen?.toMillis ? d.lastSeen.toMillis() : (d?.lastSeen || 0);
			const age = Date.now() - ts;
			setExternalActive(age < 20000);
		});
		return () => unsub();
	}, [partyId, isExternalDisplay]);

	// Heartbeat when this is the external display
	useEffect(() => {
		if (!partyId || !isExternalDisplay) return;
		let timer: number | null = null;
		const ref = doc(db, 'parties', partyId, 'control', 'displayPresence');
		const beat = async () => { try { await setDoc(ref, { lastSeen: serverTimestamp() }, { merge: true }); } catch {} };
		void beat();
		timer = window.setInterval(beat, 10000) as unknown as number;
		const unload = () => { try { void setDoc(ref, { lastSeen: serverTimestamp() }, { merge: true }); } catch {} };
		window.addEventListener('beforeunload', unload);
		return () => { if (timer) clearInterval(timer); window.removeEventListener('beforeunload', unload); };
	}, [partyId, isExternalDisplay]);

	// Subscribe to explicit external display request flag
	useEffect(() => {
		if (!partyId) return;
		const ref = doc(db, 'parties', partyId, 'control', 'displayControl');
		const unsub = onSnapshot(ref, (snap) => {
			const d = snap.data() as any;
			setExternalRequested(!!d?.externalRequested);
		});
		return () => unsub();
	}, [partyId]);

	// Playback routing: default to host screen; only move to external if host explicitly requested and it's active
	const playbackWindow = useMemo(() => {
		if (isExternalDisplay) return externalRequested; // external display can play without being signed in as host
		if (!isPartyOwner) return false;
		return !(externalRequested && externalActive); // host plays unless requested and active externally
	}, [isPartyOwner, isExternalDisplay, externalRequested, externalActive]);

	// Listen for local track events (host window only)
	useEffect(() => {
		if (!(isPartyOwner && !isExternalDisplay)) return;
		const onSelect = (e: any) => {
			const detail = e.detail || {};
			if (detail && detail.kind === 'cdg') {
				const { mp3Url, cdgBuffer, title } = detail;
				if (mp3Url && cdgBuffer) setLocalTrack({ kind: 'cdg', mp3Url, cdgBuffer, name: title || 'Local Track' });
				return;
			}
			const { url, name } = detail; if (!url) return;
			setLocalTrack({ kind: 'file', url, name: name || 'Local Track' });
		};
		const onClear = () => {
			setLocalTrack((prev) => {
				if (prev) {
					try {
						if (prev.url) URL.revokeObjectURL(prev.url);
						if (prev.mp3Url) URL.revokeObjectURL(prev.mp3Url);
					} catch {}
				}
				return null;
			});
		};
		window.addEventListener('qroke:localTrackSelected', onSelect as any);
		window.addEventListener('qroke:localTrackClear', onClear);
		return () => {
			window.removeEventListener('qroke:localTrackSelected', onSelect as any);
			window.removeEventListener('qroke:localTrackClear', onClear);
		};
	}, [isPartyOwner, isExternalDisplay]);

	// If the current song is a local catalog entry and this is the host playback window, auto-play via local library
	useEffect(() => {
		if (!(isPartyOwner && !isExternalDisplay)) return;
		const song: any = currentSong as any;
		if (!song) return;
		if (song.source === 'local') {
			try {
				const map: Map<string, any> | undefined = (window as any).qrokeLocalLibrary;
				const key = song.localId || song.id;
				if (!map) {
					console.warn('[LocalPlayback] qrokeLocalLibrary not set. Open Host Controls → Sources and Connect Folder.');
					return;
				}
				if (map && key && map.has(key)) {
					const ref: any = map.get(key);
					if (ref && ref.mp3 && ref.cdg) {
						Promise.all([ref.mp3.getFile(), ref.cdg.getFile()]).then(async ([mp3File, cdgFile]: [File, File]) => {
							const mp3Url = URL.createObjectURL(mp3File);
							const cdgBuffer = await cdgFile.arrayBuffer();
							setLocalTrack({ kind: 'cdg', mp3Url, cdgBuffer, name: song.title || 'Local Track' });
						}).catch(() => {
							console.warn('[LocalPlayback] Failed to open CD+G files');
						});
					} else {
						(ref as FileSystemFileHandle).getFile().then((file: File) => {
							const url = URL.createObjectURL(file);
							setLocalTrack({ kind: 'file', url, name: song.title || file.name || 'Local Track' });
						}).catch(() => {
							console.warn('[LocalPlayback] Failed to open local file');
						});
					}
				}
			} catch {}
		}
	}, [currentSong?.id, isPartyOwner, isExternalDisplay]);

	// Ensure local video starts despite autoplay policies
	useEffect(() => {
		if (!(isPartyOwner && !isExternalDisplay)) return;
		if (!localTrack || localTrack.kind !== 'file') { setNeedsPlaybackUnlock(false); return; }
		if (!playbackStarted) return;
		const el = localVideoRef.current; if (!el) return;
		const attempt = async () => {
			try {
				el.muted = false;
				await el.play();
				setNeedsPlaybackUnlock(false);
			} catch {
				setNeedsPlaybackUnlock(true);
			}
		};
		void attempt();
	}, [localTrack, playbackStarted, isPartyOwner, isExternalDisplay]);
    // Handle video end - advance to next song
	const handleVideoEnd = async () => {
	if (!partyId || !playbackWindow) return;
		try {
			if (reducedModeEnabled) {
				await advanceReducedPartySong(partyId);
			} else {
				const nextSongFunction = httpsCallable(functions, 'party');
				await nextSongFunction({
					action: 'next',
					partyId
				});
			}
			console.log('Successfully advanced to next song');
		} catch (error) {
			console.error('Error advancing to next song:', error);
		}
	};

	useEffect(() => {
		const timer = setTimeout(() => setShowTitleScreen(false), 2500);
		return () => clearTimeout(timer);
	}, []);

	// Listen for scheduled venue ads on the external display (host display only)
	useEffect(() => {
		if (!partyId || !playbackWindow) return;
		const ref = doc(db, 'parties', partyId, 'control', 'scheduledAd');
		const unsub = onSnapshot(ref, (snap) => {
			if (snap.exists()) {
				const d = snap.data() as any;
				setScheduledAd({ type: d.type, status: d.status ?? 'scheduled', durationSeconds: d.durationSeconds });
			} else {
				setScheduledAd(null);
			}
		});
		return () => unsub();
	}, [partyId, playbackWindow]);

	// Party-level Ads: centralized gating
	const { adsAllowed } = usePartyAdsAllowed();
	useEffect(() => {
		if (!currentParty || !isPartyOwner) return;
		if (!adsAllowed) return; // ads disabled

		const scheduleAd = () => {
			const adInterval = (10 + Math.random() * 5) * 60 * 1000; // 10-15 minutes
			adTimer.current = setTimeout(() => {
				const types: AdTypeKey[] = adTypeKeys;
				setAdType(types[Math.floor(Math.random() * types.length)]);
				setShowAd(true);
				scheduleAd();
			}, adInterval);
		};

		// First ad after 5 minutes
		adTimer.current = setTimeout(() => {
			setAdType('house');
			// Randomize duration 30–45s to cycle through 3 creatives comfortably
			setAdDurationSeconds(30 + Math.floor(Math.random() * 16));
			setShowAd(true);
			scheduleAd();
		}, 5 * 60 * 1000);

		return () => {
			if (adTimer.current) clearTimeout(adTimer.current);
		};
	}, [currentParty?.id, isPartyOwner, adsAllowed]);

	// Host heartbeat to keep party alive while host is present
	useEffect(() => {
	// Keep the party alive from the host controls window (not from the display window)
	if (!partyId || !(isPartyOwner && !isExternalDisplay)) return;
		// ...existing code for heartbeat...
	}, [partyId, isPartyOwner, isExternalDisplay]);

	// ...other effects and logic from PartyDisplayPage, excluding all phone mic code...

	// Load party if not already loaded: call the join callable and fetch the party doc so PartyContext can subscribe.
	useEffect(() => {
		if (!partyId) return;
		if (currentParty && currentParty.id === partyId) return;

		let cancelled = false;
		const loadParty = async () => {
			try {
				if (partyId.startsWith('demo-')) {
					setCurrentParty({
						id: partyId,
						name: 'Demo Karaoke Party',
						createdBy: 'demo-user',
						createdAt: new Date(),
						settings: { maxQueueSize: 50, allowDuplicates: false, requireAuth: false },
						currentSong: null,
						isActive: true
					} as any);
					return;
				}

				if (reducedModeEnabled) {
					const result = await joinReducedParty(partyId);
					if (!cancelled) {
						setCurrentParty(result.party as any);
					}
				} else {
					const joinPartyFunction = httpsCallable(functions, 'party');
					await joinPartyFunction({ action: 'join', partyId });

					const partySnap = await getDoc(doc(db, 'parties', partyId));
					if (partySnap.exists() && !cancelled) {
						const partyData = partySnap.data() as any;
						setCurrentParty(mapPartySnapshot(partySnap.id, partyData) as any);
					}
				}
			} catch (error) {
				console.error('Error loading party in regular page:', error);
			}
		};

		void loadParty();
		return () => { cancelled = true; };
	}, [partyId, currentParty?.id, setCurrentParty]);

	// First-visit coach: open Host Controls on initial entry for the host (non-display)
	useEffect(() => {
		if (!partyId) return;
		if (!reducedModeEnabled) return;
		if (!isPartyOwner) return;
		if (currentSong) return;
		if (!songQueue || songQueue.length === 0) return;

		void advanceReducedPartySong(partyId).catch((error) => {
			console.error('Reduced mode auto-start failed:', error);
		});
	}, [partyId, currentSong?.id, songQueue?.length, isPartyOwner]);

	useEffect(() => {
		if (!partyId) return;
		if (!(isPartyOwner && !isExternalDisplay)) return;
		if (reducedModeEnabled) return;
		const key = `qroke:coach:micSetupShown:${partyId}`;
		const already = localStorage.getItem(key) === '1';
		const disabled = localStorage.getItem('qroke:coach:disableMicSetup') === '1';
		if (!already && !disabled) {
			setShowHostControls(true);
			setShowMicCoach(true);
			try { localStorage.setItem(key, '1'); } catch {}
		}
	}, [partyId, isPartyOwner, isExternalDisplay]);

	// When the next song arrives, run venue ad (if scheduled) as a spacer BEFORE the splash; otherwise show splash immediately
	useEffect(() => {
		const newId = currentSong ? (currentSong as any).id : null;
		if (newId && newId !== prevSongId.current) {
			setSplashCountdown(10);
			setPlaybackStarted(false);
			// If a scheduled ad exists, play it now (spacer), then start splash on close
			if (partyId && playbackWindow && scheduledAd && scheduledAd.status === 'scheduled') {
				// Defer splash until ad finishes
				splashPendingRef.current = true;
				if (scheduledAd.type === 'venue') {
					setShowVenueAd(true);
					venueAdActiveRef.current = true;
					void setDoc(doc(db, 'parties', partyId, 'control', 'scheduledAd'), { status: 'playing', startedAt: serverTimestamp() }, { merge: true });
				} else {
					setAdType('house');
					setAdDurationSeconds(scheduledAd.durationSeconds ?? 25);
					venueAdActiveRef.current = true;
					setShowAd(true);
					void setDoc(doc(db, 'parties', partyId, 'control', 'scheduledAd'), { status: 'playing', startedAt: serverTimestamp() }, { merge: true });
				}
			} else {
				setShowSplash(true);
			}
		}
		prevSongId.current = newId;
	}, [currentSong?.id, partyId, playbackWindow, scheduledAd?.status, scheduledAd?.type]);

	// Handle splash countdown with pause support
	useEffect(() => {
		if (showSplash && splashCountdown > 0 && !splashPaused) {
			const timer = setTimeout(() => {
				setSplashCountdown(splashCountdown - 1);
			}, 1000);
			return () => clearTimeout(timer);
		}
		if (showSplash && splashCountdown === 0) {
			setShowSplash(false);
		}
	}, [showSplash, splashCountdown, splashPaused]);

	// Note: venue ad scheduling is handled BEFORE splash; the old splash-triggered ad logic is removed

	// Track when our venue ad actually played to enforce minInterval and reset counter
	useEffect(() => {
		if (!venueAdActiveRef.current) return;
		// When overlay closes we set 'played' and clear ref in onClose; update lastVenueAdAt there too
	}, [venueAdActiveRef.current]);

	// Increment songsSinceVenueAd on song change
	useEffect(() => {
		if (!currentSong) return;
		songsSinceVenueAdRef.current = (songsSinceVenueAdRef.current || 0) + 1;
	}, [currentSong?.id]);

	// Auto-schedule venue ads based on config (after X songs or no queue), with debounce and premium gating
	useEffect(() => {
		if (!partyId || !playbackWindow) return;
		if (!isPremiumHost) return;
		if (!venueConfig?.enabled) return;
		const now = Date.now();
		const minMs = Math.max(0, (venueConfig.minIntervalMinutes || 0) * 60 * 1000);
		const lastAt = lastVenueAdAtRef.current || 0;
		const withinCooldown = minMs > 0 && (now - lastAt) < minMs;
		// Do not schedule if an ad is already scheduled/playing
		const busy = !!scheduledAd && (scheduledAd.status === 'scheduled' || scheduledAd.status === 'playing');
		if (busy || withinCooldown || schedulingRef.current) return;

		const wantAfterSongs = (venueConfig.triggers?.afterSongsCount || 0) > 0 && songsSinceVenueAdRef.current >= (venueConfig.triggers.afterSongsCount || 0);
		const wantWhenNoQueue = !!venueConfig.triggers?.whenNoQueue && (!currentSong && (songQueue?.length || 0) === 0);

		if (wantAfterSongs || wantWhenNoQueue) {
			schedulingRef.current = true;
			void setDoc(doc(db, 'parties', partyId, 'control', 'scheduledAd'), {
				type: 'venue',
				status: 'scheduled',
				requestedAt: serverTimestamp(),
				durationSeconds: venueConfig.defaultDurationSeconds || 25,
			}, { merge: true }).catch(() => {}).finally(() => {
				schedulingRef.current = false;
				// Reset the counter only when we scheduled due to afterSongs rule; actual play updates lastAt
				if (wantAfterSongs) songsSinceVenueAdRef.current = 0;
			});
		}
	}, [partyId, playbackWindow, isPremiumHost, venueConfig.enabled, venueConfig.minIntervalMinutes, venueConfig.triggers?.afterSongsCount, venueConfig.triggers?.whenNoQueue, venueConfig.defaultDurationSeconds, scheduledAd?.status, currentSong?.id, songQueue?.length]);

	// Viewers: show a light ad only during splash when ads are allowed
	useEffect(() => {
		if (!currentParty) return;
		if (!adsAllowed) return;
		if (isPartyOwner) return; // viewers only
		if (showSplash) {
			setAdType('lean');
			// Tie duration to splash countdown; cap to 12s to avoid overextending
			setAdDurationSeconds(Math.min(12, Math.max(6, splashCountdown)));
			setShowAd(true);
		} else {
			setShowAd(false);
		}
	}, [adsAllowed, isPartyOwner, showSplash]);

	// When splash ends and there is a currentSong, auto-start playback.
	// Songs with 'ready' status are automatically started (no manual Play Now step).
	useEffect(() => {
		if (!showSplash && currentSong) {
			const songStatus = (currentSong as any)?.status;
			if (songStatus === 'playing' || songStatus === 'ready') {
				if (songStatus === 'ready' && partyId) {
					const startSong = reducedModeEnabled
						? startReducedPartySong(partyId, (currentSong as any).id)
						: httpsCallable(functions, 'party')({ action: 'startSong', partyId });
					startSong.catch((err: any) =>
						console.error('Auto-start failed:', err)
					);
				}
				setPlaybackStarted(true);
			}
		}
	}, [showSplash, currentSong, partyId]);

	if (!currentParty) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-qr-dark">
				<div className="text-center text-white">
					<h1 className="text-2xl font-bold mb-4">Party Not Found</h1>
					<button
						onClick={() => navigate('/')}
						className="bg-qr-primary hover:bg-qr-secondary px-6 py-2 rounded-lg text-white font-medium transition-colors"
					>
						Go Home
					</button>
				</div>
			</div>
		);
	}

	 const qrPath = `/search/${partyId}`;
	 const qrUrl = `https://qroke-a6fed.firebaseapp.com${qrPath}`;

	 const pageBackgroundStyle = hostBranding?.backgroundUrl
		 ? { backgroundImage: `url(${hostBranding.backgroundUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
		 : undefined;
	 const pageFontStyle = hostBranding?.fontFamily ? { fontFamily: hostBranding.fontFamily } : undefined;

	 return (
		 <div
			 className="min-h-screen bg-gradient-to-br from-purple-900 via-qr-accent/40 to-purple-900 text-white flex flex-col items-center justify-center relative"
			 style={{ ...(pageBackgroundStyle || {}), ...(pageFontStyle || {}) }}
		 >
			 {hostBranding?.backgroundUrl && <div className="absolute inset-0 bg-black/50" />}
			 <a href="/" className="absolute top-6 left-6 z-50">
				 {hostBranding?.logoUrl ? (
					 <img src={hostBranding.logoUrl} alt={hostBranding.venueName || 'Venue Logo'} className="h-12 object-contain drop-shadow" />
				 ) : (
					 <img src="/Qroke Logo.png" alt="QrOke Logo" className="h-12 opacity-50 hover:opacity-100 transition-opacity" />
				 )}
			 </a>
			 {/* Host/Guest Status Indicator */}
				 <div className="absolute top-6 right-6 z-50 flex items-center space-x-3">
					 <div className={`px-4 py-2 rounded-lg text-sm font-semibold ${
						 (isPartyOwner && !isExternalDisplay)
							 ? 'bg-green-600/80 text-green-100'
							 : (isPartyOwner && isExternalDisplay)
								 ? 'bg-purple-600/80 text-purple-100'
								 : 'bg-blue-600/80 text-blue-100'
					 }`}>
						 {(isPartyOwner && !isExternalDisplay) ? '🎤 HOST' : (isPartyOwner && isExternalDisplay ? '🖥️ DISPLAY' : '👀 VIEWER')}
					 </div>
					 {isPartyOwner && (
						 <div className="flex items-center space-x-2">
							 {!isExternalDisplay && (
								 <button
									onClick={() => setShowHostControls(true)}
									 className="p-2 bg-blue-600/80 hover:bg-blue-700/80 text-white rounded-lg transition-colors"
									 title="Host Controls"
									 id="host-controls-btn"
									 style={hostBranding?.primaryColor ? { backgroundColor: hostBranding.primaryColor } : undefined}
								 >
									 🎛️
								 </button>
							 )}
							 {/* Offer Cast option for mobile hosts as an alternative to External Display */}
							 {!isExternalDisplay && isMobileHost && (
								 <button
									 onClick={() => setShowCastOptions(true)}
									 className="p-2 bg-purple-600/80 hover:bg-purple-700/80 text-white rounded-lg transition-colors"
									 title="Cast to TV"
									 style={hostBranding?.secondaryColor ? { backgroundColor: hostBranding.secondaryColor } : undefined}
								 >
									 📺
								 </button>
							 )}
							 <button
								 onClick={() => setShowQrBox(prev => !prev)}
								 className="p-2 bg-gray-600/80 hover:bg-gray-700/80 text-white rounded-lg transition-colors"
								 title={showQrBox ? 'Hide QR Code' : 'Show QR Code'}
								 style={(hostBranding?.secondaryColor || hostBranding?.primaryColor) ? { backgroundColor: hostBranding?.secondaryColor || hostBranding?.primaryColor } : undefined}
							 >
								 📱
							 </button>
						 </div>
					 )}
					{!isPartyOwner && user && (
						<div className="flex items-center space-x-2">
							<button
								onClick={() => setShowUserSongManager(true)}
								className="p-2 bg-blue-600/80 hover:bg-blue-700/80 text-white rounded-lg transition-colors"
								title="My Songs"
								style={hostBranding?.primaryColor ? { backgroundColor: hostBranding.primaryColor } : undefined}
							>
								🎵
							</button>
							<button
								onClick={() => setShowQrBox(prev => !prev)}
								className="p-2 bg-gray-600/80 hover:bg-gray-700/80 text-white rounded-lg transition-colors"
								title={showQrBox ? 'Hide QR Code' : 'Show QR Code'}
								style={(hostBranding?.secondaryColor || hostBranding?.primaryColor) ? { backgroundColor: hostBranding?.secondaryColor || hostBranding?.primaryColor } : undefined}
							>
								📱
							</button>
						</div>
					)}
			 </div>

			 {/* Main video/content display */}
								 <div className="w-screen h-screen flex items-center justify-center">
										 {currentSong && playbackStarted ? (
												 playbackWindow ? (
													 (currentSong as any)?.source === 'local' ? (
														 <div className="w-full h-full flex items-center justify-center">
															 <div className="max-w-5xl w-full p-4">
																 {localTrack?.kind === 'cdg' ? (
																	 <div className="relative">
																		 <CdgPlayer
																			 mp3Url={localTrack.mp3Url!}
																			 cdgBuffer={localTrack.cdgBuffer!}
																			 onEnded={handleVideoEnd}
																		 />
																	 </div>
																 ) : localTrack?.kind === 'file' ? (
																	 <>
																		 <video
																			 ref={localVideoRef}
																			 src={localTrack.url}
																			 controls
																			 autoPlay
																			 playsInline
																			 preload="auto"
																			 className="w-full rounded-lg shadow-lg bg-black max-h-[70vh]"
																			 onPlay={() => setNeedsPlaybackUnlock(false)}
																			 onError={() => setNeedsPlaybackUnlock(true)}
																			 onEnded={handleVideoEnd}
																		 />
																		 {needsPlaybackUnlock && (
																			 <div className="mt-3 text-center">
																				 <button
																					 className="px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white"
																					 onClick={async () => {
																						 const el = localVideoRef.current; if (!el) return;
																						 try { await el.play(); setNeedsPlaybackUnlock(false); } catch {}
																					 }}
																				 >
																					 ▶ Click to start playback
																				 </button>
																			 </div>
																		 )}
																	 </>
																 ) : (
																	 <div className="text-center bg-black/50 rounded-lg p-8 max-w-xl mx-auto">
																		 <h3 className="text-xl font-bold mb-2">Connect Local Library</h3>
																		 <p className="text-gray-300">Open Host Controls → Sources and connect your folder to play this local song.</p>
																	 </div>
																 )}
															 </div>
														 </div>
													 ) : (
														 <YouTubePlayer
															 currentSong={currentSong}
															 onVideoEnd={handleVideoEnd}
															 showSplash={showSplash}
															 videoDuration={videoDuration || undefined}
														 />
													 )
												 ) : (
						 <div className="text-center flex flex-col items-center justify-center h-full">
							 <div className="bg-black/50 rounded-lg p-8 max-w-4xl mx-auto">
								 <img 
									 src={currentSong.thumbnail} 
									 alt={currentSong.title}
									 className="w-96 h-54 object-cover rounded-lg mx-auto mb-6"
								 />
								 <h2 className="text-4xl font-bold text-white mb-2">{currentSong.title}</h2>
								 <h3 className="text-2xl text-gray-300 mb-6">{currentSong.artist}</h3>
								 <p className="text-lg text-gray-400 mb-4">Singer: {currentSong.singerName}</p>
								 <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4">
									 <p className="text-yellow-200 text-lg">
										 🎵 Currently playing on host's screen
									 </p>
									 <p className="text-yellow-300 text-sm mt-2">
										 Audio is controlled by the party host to prevent sync issues
									 </p>
								 </div>
							 </div>
						 </div>
					 )
				 ) : !showSplash && !currentSong ? (
					 <div className="text-center flex flex-col items-center">
									 <img src={hostBranding?.logoUrl || '/QrOke-Mascot.png'} alt={hostBranding?.venueName || 'QrOke Logo'} className="w-2/3 max-w-md mb-8 object-contain" />
						 <p className="text-xl text-gray-300">
							 Scan the QR code or visit the link to request songs
						 </p>
					 </div>
				 ) : null}
			 </div>

			 {/* Play Now button for songs in 'ready' state */}
			 {!showSplash && isPartyOwner && currentSong && (currentSong as any)?.status === 'ready' && (
				 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
					 <div className="bg-gradient-to-br from-yellow-50 to-green-50 border-4 border-green-500 rounded-2xl p-12 text-center max-w-2xl shadow-2xl">
						 <h2 className="text-5xl font-bold text-green-700 mb-4">Ready to Start!</h2>
						 <p className="text-2xl text-gray-800 font-semibold mb-2">Up Next: {currentSong.title}</p>
						 <p className="text-xl text-gray-700 mb-8">Singer: {currentSong.singerName}</p>
						 <button
							 onClick={async () => {
								 try {
									 if (reducedModeEnabled) {
										 await startReducedPartySong(partyId!, (currentSong as any).id);
									 } else {
										 const partyFunction = httpsCallable(functions, 'party');
										 await partyFunction({ action: 'startSong', partyId });
									 }
								 } catch (error: any) {
									 console.error('Failed to start song:', error);
									 alert('Failed to start song: ' + (error.message || 'Unknown error'));
								 }
							 }}
							 className="bg-green-500 hover:bg-green-400 text-white border-none rounded-xl px-16 py-6 text-3xl font-bold shadow-2xl cursor-pointer transition-all transform hover:scale-105"
						 >
							 ▶️ Play Now
						 </button>
						 <p className="text-gray-600 text-sm mt-6">This gives you time to announce the singer and let them get ready!</p>
					 </div>
				 </div>
			 )}

						 {/* Queue display showing upcoming songs */}
						 <QueueDisplay queue={songQueue} />

						 {/* Splash Screen Overlay */}
						 <SplashScreen
							 currentSong={currentSong || (songQueue && songQueue.length > 0 ? songQueue[0] : null)}
							 countdown={splashCountdown}
							 show={showSplash}
							 branding={hostBranding}
						 />


			 {/* QR code for song requests */}
			{showQrBox && (
				<QRCodeDisplay 
					value={qrUrl}
					size={100}
					compact
					className="w-[120px]"
					onClick={() => navigate(qrPath)}
				/>
			)}


			 {/* QR Code Modal */}
			 {/* legacy modal removed — QR box is non-blocking and toggleable */}

			 {/* Host Controls Modal */}
			 {showHostControls && (
				 <HostControlsModal 
					 isOpen={showHostControls}
					 onClose={() => setShowHostControls(false)}
					 partyId={(partyId || currentParty?.id || '')}
					 // ...other props as needed for regular mode...
					 splashCountdown={splashCountdown}
					 onSplashCountdownChange={setSplashCountdown}
					 splashPaused={splashPaused}
					 onSplashPausedChange={setSplashPaused}
					 showSetupTip={showMicCoach}
					 onDismissSetupTip={() => setShowMicCoach(false)}
					 onCloseWithMicStatus={(anyLive) => { if (!anyLive) pushToast('Tip: Turn on a microphone (Go Live) in Host Controls. Open External Display if you’ll use another screen.', 'info', 6000); }}
				/>
			 )}

			 {/* User Song Manager Modal */}
			 {showUserSongManager && user && (
				 <UserSongManager 
					 partyId={partyId || ''}
					 isOpen={showUserSongManager}
					 onClose={() => setShowUserSongManager(false)}
				 />
			 )}

			{/* Ads overlay (party-level gating + venue ad) */}
			<QrOkeAd 
				show={showAd}
				onClose={async () => {
					setShowAd(false);
					if (venueAdActiveRef.current && partyId && playbackWindow) {
						venueAdActiveRef.current = false;
						try {
							await setDoc(doc(db, 'parties', partyId, 'control', 'scheduledAd'), { status: 'played', playedAt: serverTimestamp() }, { merge: true });
							lastVenueAdAtRef.current = Date.now();
						} catch {}
						if (splashPendingRef.current) {
							splashPendingRef.current = false;
							setShowSplash(true);
						}
					}
				}}
				adType={adType}
				cycle
				durationSeconds={adDurationSeconds}
			/>

            {/* Cast options modal for mobile hosts */}
            {showCastOptions && (
                <CastOptionsModal
                    isOpen={showCastOptions}
                    onClose={() => setShowCastOptions(false)}
                    currentSong={currentSong}
                />
            )}
			{/* Venue Ad Overlay (premium host-configured sources) */}
			<VenueAdOverlay
				show={showVenueAd}
				onClose={async () => {
					setShowVenueAd(false);
					if (venueAdActiveRef.current && partyId && playbackWindow) {
						venueAdActiveRef.current = false;
						try {
							await setDoc(doc(db, 'parties', partyId, 'control', 'scheduledAd'), { status: 'played', playedAt: serverTimestamp() }, { merge: true });
							lastVenueAdAtRef.current = Date.now();
						} catch {}
						if (splashPendingRef.current) {
							splashPendingRef.current = false;
							setShowSplash(true);
						}
					}
				}}
				config={venueConfig}
				durationSeconds={scheduledAd?.durationSeconds || venueConfig.defaultDurationSeconds}
			/>
				 {/* Toasts */}
				 {toasts.length > 0 && (
					 <div className="fixed top-4 right-4 z-[60] space-y-2">
						 {toasts.map(t => (
							 <div key={t.id} className={`px-4 py-2 rounded shadow-lg text-sm ${t.type === 'error' ? 'bg-red-700/90' : t.type === 'success' ? 'bg-green-700/90' : 'bg-gray-800/90'} text-white border border-white/10`}>{t.text}</div>
						 ))}
					 </div>
				 )}
				 </div>
	 );
};

export default PartyDisplayPageRegular;
// Removed duplicate component and export